/**
 * schedulePipelineWarm — debounced whole-scene shader-pipeline pre-compile.
 *
 * Turning the camera reveals objects that have never rendered, and three r182
 * compiles their pipelines SYNCHRONOUSLY on first view — the "freezes when I
 * look in a new direction" report (2026-07-21). The biggest factory is chunk
 * foliage: every chunk CLONES its materials for per-chunk wind, so ~4 clones ×
 * 49 chunks each compile on first sight. compileAsync walks the scene and
 * compiles every pending pipeline off the critical path (KHR parallel compile /
 * WebGPU async pipelines); one trailing call batches a burst of scatters.
 */
import type { App } from 'enginex';
import type * as THREE from 'three/webgpu';

let timer: number | null = null;

// ── Texture pre-upload queue ─────────────────────────────────────────────────
// KTX2/compressed textures upload to the GPU at FIRST BIND (profiled: a 6.3 s
// compressedTexSubImage2D burst at area arrival). renderer.initTexture() (r182)
// uploads eagerly; draining a queue 2-per-32 ms moves those uploads into the
// warm window (loading screen / warm ring) instead of the arrival frames.
const texQueue: THREE.Texture[] = [];
const texSeen = new Set<string>();
let texTimer: number | null = null;

export function queueTextureWarm(app: App, root: { traverse(cb: (o: unknown) => void): void }): void {
  root.traverse((o) => {
    const mat = (o as { material?: THREE.Material | THREE.Material[] }).material;
    for (const m of Array.isArray(mat) ? mat : mat ? [mat] : []) {
      for (const v of Object.values(m as unknown as Record<string, unknown>)) {
        const t = v as THREE.Texture | null | undefined;
        if (t && (t as { isTexture?: boolean }).isTexture && !texSeen.has(t.uuid)) {
          texSeen.add(t.uuid);
          // Tombstone on dispose: initTexture on an already-disposed texture
          // would re-create its GPU copy with no owner left to free it.
          t.addEventListener('dispose', () => { (t as unknown as { __warmDead?: boolean }).__warmDead = true; });
          texQueue.push(t);
        }
      }
    }
  });
  if (texTimer === null && texQueue.length > 0) drainTextures(app);
}

function drainTextures(app: App): void {
  texTimer = window.setTimeout(() => {
    texTimer = null;
    const r = app.renderer as unknown as { native?: { initTexture?: (t: THREE.Texture) => void } };
    // Time-budgeted drain: a fixed 2-per-tick starved behind a streaming city
    // (mip-capped 512² uploads are ~0.3 ms — first bind kept winning the race).
    // ~5 ms per 32 ms window drains a whole settlement's textures in ~2 s
    // while never itself becoming the hitch.
    const start = performance.now();
    while (performance.now() - start < 5) {
      const t = texQueue.shift();
      if (!t) break;
      if ((t as unknown as { __warmDead?: boolean }).__warmDead) continue; // disposed while queued
      try {
        r.native?.initTexture?.(t);
        // F15 heap-plateau fix: after the eager GPU upload the CPU-side
        // compressed mipmap payload is dead weight — 2-5 MB × hundreds of KTX2
        // textures was the bulk of the ~3 GB steady-state heap. Compressed
        // textures only (their arrays are never read back for raycast etc.);
        // escape hatch localStorage 'atlas:keep-texture-cpu' (device-loss
        // re-upload debugging).
        const ct = t as unknown as { isCompressedTexture?: boolean; mipmaps?: unknown[] };
        if (ct.isCompressedTexture && Array.isArray(ct.mipmaps) && localStorage.getItem('atlas:keep-texture-cpu') !== '1') {
          ct.mipmaps.length = 0;
        }
      } catch { /* warm is never load-bearing */ }
    }
    if (texQueue.length > 0) drainTextures(app);
  }, 32);
}

// Targeted warm queue: batch-birth hooks pass the newborn mesh so the flush
// compiles ONLY those objects (compileAsync(target, cam, scene) — the 3rd arg
// keeps the real scene's lights/env context). A call without a target falls
// back to the legacy whole-scene sweep — live-profiled 2026-07-31: each
// whole-scene compileAsync paid an O(scene) walk (~400-650 ms in a built
// city) REGARDLESS of how few pipelines were pending; the sweep, not the
// builds, dominated both the settlement warm and the post-scatter hitches.
const pendingWarm = new Set<unknown>();
let wholeSceneWarm = false;

export function schedulePipelineWarm(app: App, target?: unknown): void {
  if (target) pendingWarm.add(target);
  else wholeSceneWarm = true;
  if (timer !== null) return;
  timer = window.setTimeout(() => {
    timer = null;
    const targets = wholeSceneWarm ? [app.world.scene] : [...pendingWarm];
    pendingWarm.clear();
    wholeSceneWarm = false;
    try {
      const r = app.renderer as unknown as {
        native?: { compileAsync?: (scene: unknown, camera: unknown, targetScene?: unknown) => Promise<unknown> };
        cameras?: { main?: unknown };
      };
      const cam = r.cameras?.main;
      if (r.native?.compileAsync && cam) {
        const compile = r.native.compileAsync;
        // Sequential, not parallel — simultaneous compiles crowd one frame.
        void (async () => {
          for (const t of targets) {
            try { await compile(t, cam, app.world.scene); } catch { /* best-effort */ }
          }
        })();
      }
    } catch { /* warm is never load-bearing */ }
  }, 600);
}

// ── Byte-budgeted resource residency (LOD-switch research S1/S2, 2026-09-02) ──
// compileAsync's SYNCHRONOUS prefix uploads an object's geometry attributes
// and binds (uploads) its textures in one task — a LOD0 building with a
// handful of 1024 px mip chains was a single multi-megabyte upload task, the
// measured switch hitch. This queue makes an object's resources RESIDENT
// first, a bounded number of bytes per task (textures via the public
// initTexture, attributes via the renderer's attribute manager), yielding
// between tasks; the compile that follows then only links the pipeline.
// The same queue pre-uploads a level the lookahead predicts (S2) so the
// eventual swap is a rebind.
export interface WarmResource { kind: 'tex' | 'attr' | 'index'; ref: THREE.Texture | THREE.BufferAttribute; bytes: number }
/** Bytes uploaded per task before yielding (3 MB ≈ a few ms of memcpy on a desktop GPU driver). */
export const WARM_BYTES_PER_TASK = 3 * 1024 * 1024;
const uploaded = new WeakSet<object>();

function textureBytes(t: THREE.Texture): number {
  const ct = t as unknown as { isCompressedTexture?: boolean; mipmaps?: Array<{ data?: ArrayBufferView }>; image?: { width?: number; height?: number } };
  if (ct.isCompressedTexture && Array.isArray(ct.mipmaps)) { let b = 0; for (const m of ct.mipmaps) b += m.data?.byteLength ?? 0; return b; }
  const w = ct.image?.width ?? 0, h = ct.image?.height ?? 0;
  return Math.round(w * h * 4 * 1.34);
}

/** Every GPU resource an object will need at first draw, deduped, with a byte size. */
export function collectResources(object: THREE.Object3D): WarmResource[] {
  const out: WarmResource[] = [];
  const seen = new Set<object>();
  object.traverse((o) => {
    const m = o as THREE.Mesh;
    const geo = m.geometry as THREE.BufferGeometry | undefined;
    if (geo) {
      for (const name of Object.keys(geo.attributes)) {
        const attr = geo.attributes[name] as THREE.BufferAttribute;
        if (seen.has(attr) || uploaded.has(attr)) continue;
        seen.add(attr);
        out.push({ kind: 'attr', ref: attr, bytes: (attr.array as ArrayBufferView).byteLength });
      }
      if (geo.index && !seen.has(geo.index) && !uploaded.has(geo.index)) { seen.add(geo.index); out.push({ kind: 'index', ref: geo.index, bytes: (geo.index.array as ArrayBufferView).byteLength }); }
    }
    const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
    for (const mat of mats) {
      for (const v of Object.values(mat as unknown as Record<string, unknown>)) {
        const t = v as THREE.Texture | null | undefined;
        if (t && (t as { isTexture?: boolean }).isTexture && !seen.has(t) && !uploaded.has(t)) { seen.add(t); out.push({ kind: 'tex', ref: t, bytes: textureBytes(t) }); }
      }
    }
  });
  return out;
}

/** Pure: group item byte sizes into consecutive batches of ≤ `budget` (an oversized item stands alone). */
export function planUploadBatches(bytes: number[], budget: number): number[][] {
  const batches: number[][] = [];
  let cur: number[] = [], sum = 0;
  for (let i = 0; i < bytes.length; i++) {
    if (cur.length > 0 && sum + bytes[i] > budget) { batches.push(cur); cur = []; sum = 0; }
    cur.push(i); sum += bytes[i];
  }
  if (cur.length) batches.push(cur);
  return batches;
}

interface ResItem { res: WarmResource; done: () => void }
const resQueue: ResItem[] = [];
let resDraining = false;
export const residencyStats = { bytesUploaded: 0, resourcesUploaded: 0, uploadTasks: 0, queued: 0 };

/** Make an object's resources resident under the byte budget. Never hides or reveals anything. */
export function warmResources(app: App, object: THREE.Object3D): Promise<void> {
  const r = app.renderer as unknown as { native?: { initTexture?: (t: THREE.Texture) => void; _attributes?: { update: (a: THREE.BufferAttribute, type: number) => void } } };
  if (!r.native?.initTexture) return Promise.resolve();
  const items = collectResources(object);
  if (items.length === 0) return Promise.resolve();
  let left = items.length;
  return new Promise<void>((resolve) => {
    const done = (): void => { if (--left === 0) resolve(); };
    for (const res of items) resQueue.push({ res, done });
    residencyStats.queued = resQueue.length;
    if (!resDraining) void drainResources(app);
  });
}

async function drainResources(app: App): Promise<void> {
  resDraining = true;
  const r = app.renderer as unknown as { native?: { initTexture?: (t: THREE.Texture) => void; _attributes?: { update: (a: THREE.BufferAttribute, type: number) => void } } };
  try {
    while (resQueue.length) {
      let bytes = 0;
      // At least one item per task; then as many as the budget allows.
      while (resQueue.length && (bytes === 0 || bytes + resQueue[0].res.bytes <= WARM_BYTES_PER_TASK)) {
        const { res, done } = resQueue.shift()!;
        if (!uploaded.has(res.ref)) {
          try {
            if (res.kind === 'tex') r.native?.initTexture?.(res.ref as THREE.Texture);
            else r.native?._attributes?.update(res.ref as THREE.BufferAttribute, res.kind === 'index' ? 2 : 1); // AttributeType.INDEX / VERTEX
            uploaded.add(res.ref);
            residencyStats.bytesUploaded += res.bytes; residencyStats.resourcesUploaded++;
          } catch { /* the compile prefix will retry it */ }
        }
        bytes += res.bytes;
        done();
      }
      residencyStats.uploadTasks++;
      residencyStats.queued = resQueue.length;
      await yieldToMain();
    }
  } finally {
    resDraining = false;
    if (resQueue.length) void drainResources(app);
  }
}

/** The pending warm of a batch object (hidden until warmed), or null when it is already showing. */
const warmState = new WeakMap<THREE.Object3D, Promise<void>>();
export function warmPending(object: THREE.Object3D | null | undefined): Promise<void> | null {
  return object ? warmState.get(object) ?? null : null;
}

// ── Warm-then-show (settlement study R4) ─────────────────────────────────────
// Anything added to the scene with a (geometry, material) pair the renderer
// has never seen links its shader SYNCHRONOUSLY at first draw and uploads its
// attributes + textures in that same frame. warmThenShow hides the object,
// compiles its pipelines + uploads its buffers off the critical path
// (compileAsync → KHR_parallel_shader_compile on WebGL, createRenderPipelineAsync
// on WebGPU), then reveals it.
//
// BUDGET: compileAsync's synchronous prefix (project + geometry + bindings =
// the uploads) runs the moment it is called, so a chunk scatter that births
// 20 batches in one frame used to pay 20 uploads in ONE task. The queue below
// drains ONE object per task with a prioritised yield between them (the
// texture queue's pattern), so the worst task is a single object's uploads.
// Objects hidden by their OWNER stay hidden (we only hide what is visible and
// only restore what we hid); a bounded wait per object guarantees a compile
// failure can never leave anything invisible.
import { yieldToMain } from './yield_main.js';
const warmStats = { warmed: 0, revealed: 0, timedOut: 0, residencyTimedOut: 0, skipped: 0, queuedMax: 0 };
const WARM_TIMEOUT_MS = 4000;
/** Residency (uploads) gets its own bound: a city's worth of resources queues behind one object, and the 4 s compile bound must not start until the object's bytes are in. */
const RESIDENCY_TIMEOUT_MS = 20_000;
interface WarmItem { object: THREE.Object3D; reveal: () => void; timer: number }
const warmQueue: WarmItem[] = [];
let warmDraining = false;
export function warmThenShow(app: App, object: THREE.Object3D, timeoutMs = WARM_TIMEOUT_MS): Promise<void> {
  const r = app.renderer as unknown as {
    native?: { compileAsync?: (scene: unknown, camera: unknown, targetScene?: unknown) => Promise<unknown> };
    cameras?: { main?: unknown };
  };
  if (!r.native?.compileAsync || !r.cameras?.main || !object.visible) { warmStats.skipped++; return Promise.resolve(); }
  object.visible = false;
  warmStats.warmed++;
  const p = new Promise<void>((resolve) => {
    let done = false;
    const reveal = () => { if (done) return; done = true; object.visible = true; warmStats.revealed++; warmState.delete(object); resolve(); };
    // S1: resources first, under the byte budget (its own bound — a whole
    // city's bytes may be queued ahead), THEN the compile under the 4 s bound
    // that starts once the object's bytes are in. Either bound reveals the
    // object unwarmed rather than never.
    let resDone = false;
    const resTimer = window.setTimeout(() => { if (!resDone && !done) { warmStats.residencyTimedOut++; reveal(); } }, RESIDENCY_TIMEOUT_MS);
    void warmResources(app, object).then(() => {
      resDone = true; window.clearTimeout(resTimer);
      if (done) return;
      const timer = window.setTimeout(() => { if (!done) warmStats.timedOut++; reveal(); }, timeoutMs);
      warmQueue.push({ object, reveal, timer });
      warmStats.queuedMax = Math.max(warmStats.queuedMax, warmQueue.length);
      if (!warmDraining) void drainWarm(app);
    });
  });
  warmState.set(object, p);
  return p;
}
async function drainWarm(app: App): Promise<void> {
  warmDraining = true;
  const r = app.renderer as unknown as {
    native?: { compileAsync?: (scene: unknown, camera: unknown, targetScene?: unknown) => Promise<unknown> };
    cameras?: { main?: unknown };
  };
  try {
    for (let item = warmQueue.shift(); item; item = warmQueue.shift()) {
      const { object, reveal, timer } = item;
      if (object.visible) { window.clearTimeout(timer); continue; } // already revealed by the bound
      // Torn down while queued (deferDisposeTree detaches first): compiling it
      // would re-upload buffers that are about to be freed, with no owner left.
      if (!object.parent) { window.clearTimeout(timer); reveal(); continue; }
      // compileAsync only awaits init() before projecting synchronously, and it
      // skips invisible objects — make the object visible for the projection
      // window only (no event-loop turn happens in between).
      object.visible = true;
      // An empty InstancedMesh (a crowd pool before its first cull-in) would
      // compile nothing; give it one instance for the projection window only.
      const im = object as THREE.Object3D & { isInstancedMesh?: boolean; count?: number };
      const emptyPool = im.isInstancedMesh === true && im.count === 0;
      if (emptyPool) im.count = 1;
      let p: Promise<unknown>;
      try { p = r.native!.compileAsync!(object, r.cameras!.main, app.world.scene); } catch { p = Promise.resolve(); }
      if (emptyPool) im.count = 0;
      object.visible = false;
      try { await p; } catch { /* warm is never load-bearing */ }
      window.clearTimeout(timer);
      reveal();
      await yieldToMain(); // one object's uploads per task
    }
  } finally {
    warmDraining = false;
    if (warmQueue.length) void drainWarm(app);
  }
}

/** Diagnostics: warm-then-show counters (`__warmStats()`). */
export function pipelineWarmStats(): { warmed: number; revealed: number; timedOut: number; residencyTimedOut: number; skipped: number; queuedMax: number; textureQueue: number; residency: typeof residencyStats } {
  return { ...warmStats, textureQueue: texQueue.length, residency: { ...residencyStats } };
}
